import { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { Mixins } from 'vue-mixin-decorator'
import ClickOutside from 'vue-click-outside'
import moment from 'moment'

import { Element } from '@src/mixins/element/element'
import { Icon } from '@src/elements/icon/icon'
import { convertLocalDateToDate, getLocalDateFormat } from '@src/util/date-format'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'

import './date-picker.sass'

@Component({
  directives: {
    ClickOutside
  },
  computed: {
    locale () {
      return this.$store.state.i18n.locale
    },
    datePickerActiveTypeFilter () {
      return this.$store.state.datePickerActiveTypeFilter
    }
  }
})
export class DatePicker extends Mixins<Element>(Element) {
  @Prop({ required: false })
  popperClass: string

  @Prop({ required: false })
  value: string

  @Prop({ required: false })
  format: string

  @Prop({ required: false, default: 'date' })
  type: string

  @Prop({ required: false, default: true })
  allowPastDate: boolean

  @Prop({ required: false, default: true })
  allowFutureDate: boolean

  @Prop({ required: false, default: false })
  readonly: boolean

  @Prop({ required: false })
  placeholder: string

  @Prop({ required: false })
  change: (value: string) => void

  // if the past/future dates are allowed, the first available date will be shown, and others will be disabled
  @Prop({ required: false })
  firstAvailableDate: Date

  @Prop({ required: false, default: true })
  appendToBody: boolean

  @Prop({ required: false, default: true })
  editable: boolean

  hasFocus: boolean = false
  actualValue: string = ''
  oldValue: string = ''
  locale
  @Prop({ required: false, default: false })
  resetBasedOnActiveFilerType: boolean
  datePickerActiveTypeFilter: DateFilterType

  @Prop({ required: false, default: null })
  filter: DateFilterType

  mounted () {
    this.actualValue = this.value
    this.oldValue = this.value

    // this is needed to prevent autocomplete on Firefox
    const inputComponent = this.$refs.input as Element
    if (inputComponent.$el) {
      inputComponent.$el.children[0].setAttribute('autocomplete', 'new-password')
    }
  }

  @Watch('value')
  onValueChange (newValue) {
    if (newValue === 'Invalid date') {
      this.actualValue = ''
      return
    }
    this.actualValue = newValue
  }

  @Watch('datePickerActiveTypeFilter')
  onActiveFilterTypeChange (newValue) {
    if (this.datePickerActiveTypeFilter !== this.filter && this.filter) {
      this.actualValue = ''
      this.oldValue = ''
    }
  }

  @Watch('locale')
  updateTitleOnLanguageChange () {
    this.$forceUpdate()
  }

  render (h: CreateElement) {
    let styleClasses = [
      ...this.elementStyles,
      ...this.inputStyles,
      'date-picker'
    ]

    return (
      this.renderElement(h, styleClasses, this.disabled, [
        this.renderInput(h, this.disabled),
        this.renderEmptyButton(h)
      ])
    )
  }

  protected get inputStyles () {
    let styleClasses = ['input']

    if (this.hasFocus) {
      styleClasses.push('focus')
    }

    return styleClasses
  }

  protected isValidDate () {
    if (this.allowPastDate && this.allowFutureDate) {
      return true
    }
    let selectedDate = convertLocalDateToDate(this.actualValue)
    if (this.type === 'week') {
      selectedDate.setDate(selectedDate.getDate() - 1)
    }

    let referenceDate = this.firstAvailableDate ? this.firstAvailableDate : new Date()

    if (!this.allowPastDate) {
      // allow today
      selectedDate.setHours(23, 59, 59)
      return selectedDate.getTime() >= referenceDate.getTime()
    }
    // istanbul ignore else: returned above when both past and future dates are allowed
    if (!this.allowFutureDate) {
      // allow today
      selectedDate.setHours(0, 0, 0)
      return selectedDate.getTime() <= referenceDate.getTime()
    }
  }

  protected disableDates (date: Date) {
    let referenceDate = this.firstAvailableDate ? this.firstAvailableDate : new Date()

    if (!this.allowPastDate) {
      referenceDate.setHours(0, 0, 0, 0)
      return date.getTime() < referenceDate.getTime()
    }
    if (!this.allowFutureDate) {
      referenceDate.setHours(0, 0, 0, 0)
      // the week picker checks the Tuesday of the clicked week, so the week is judged by its first day
      let checkedDate = this.type === 'week' ? moment(date).startOf('isoWeek').toDate() : date
      return checkedDate.getTime() > referenceDate.getTime()
    }

    return false
  }

  protected renderInput (h: CreateElement, disabled: boolean) {
    return <div class='input-container' onClick={this.onFocus} v-click-outside={this.onFocusLost}>
      <el-date-picker key={this.locale} readonly={this.readonly} ref='input' v-model={this.actualValue} attrs-onkeydown='return false'
                      append-to-body={this.appendToBody}
                      format={this.format || this.format === '' ? this.format : getLocalDateFormat(this.locale)}
                      value-format={this.format || this.format === '' ? this.format : getLocalDateFormat(this.locale)}
                      type={this.type} popper-class={this.popperClass}
                      placeholder={this.placeholder ? this.placeholder : getLocalDateFormat(this.locale).toLowerCase()}
                      onChange={this.onChange}
                      pickerOptions={{ firstDayOfWeek: 1, disabledDate: this.disableDates }}
                      editable={this.editable}
                      default-value={this.firstAvailableDate ? this.firstAvailableDate : null}/>
      {this.label ? <label class='input-description'>{this.label}</label> : ''}
    </div>
  }

  protected onFocus () {
    this.hasFocus = true
  }

  protected onFocusLost () {
    this.hasFocus = false
  }

  protected onChange () {
    if (this.change) {
      if (!this.isValidDate()) {
        this.change('')
      } else {
        this.change(this.actualValue)
      }
      this.oldValue = this.actualValue
    }
    this.onFocusLost()
  }

  protected renderEmptyButton (h: CreateElement) {
    if (!this.actualValue || this.disabled || this.readonly) {
      return
    }

    return <div class='right-icon' onClick={() => {
      this.actualValue = ''
      this.onChange()
    }}>
      <Icon name='util/close' />
    </div>
  }
}
